import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import type { Question, QuestionInsert, QuestionGroup } from '../types/database'

type QuestionGroupWithBook = QuestionGroup & {
  book: { name: string }
}

interface QuestionFormProps {
  question?: Question | null
  onClose: () => void
}

export function QuestionForm({ question, onClose }: QuestionFormProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [displayOrder, setDisplayOrder] = useState(0)
  const [questionGroupId, setQuestionGroupId] = useState<number | null>(null)
  const [questionGroups, setQuestionGroups] = useState<QuestionGroupWithBook[]>([])
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchQuestionGroups()
    if (question) {
      setName(question.name)
      setDescription(question.description || '')
      setDisplayOrder(question.display_order)
      setQuestionGroupId(question.question_group_id)
    }
  }, [question])

  const fetchQuestionGroups = async () => {
    try {
      setLoadingGroups(true)
      const { data, error } = await supabase
        .from('question_groups')
        .select(`
          *,
          book:books(name)
        `)
        .order('display_order', { ascending: true })

      if (error) throw error
      setQuestionGroups(data || [])
    } catch (err) {
      console.error('Failed to load question groups:', err)
    } finally {
      setLoadingGroups(false)
    }
  }

  const uploadFile = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop()
    const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('files')
      .upload(fileName, file)

    if (uploadError) throw uploadError

    return fileName
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setUploading(true)

    try {
      let filePath = question?.file || null

      // Upload new file if selected
      if (file) {
        // Delete old file if updating
        if (question?.file) {
          const oldFileName = question.file.split('/').pop()
          if (oldFileName) {
            await supabase.storage.from('files').remove([oldFileName])
          }
        }

        filePath = await uploadFile(file)
      }

      if (question) {
        // Update existing question
        const { error: updateError } = await supabase
          .from('questions')
          .update({
            name,
            description: description || null,
            display_order: displayOrder,
            question_group_id: questionGroupId || null,
            file: filePath,
          })
          .eq('id', question.id)

        if (updateError) throw updateError
      } else {
        // Insert new question
        const newQuestion: QuestionInsert = {
          name,
          description: description || null,
          display_order: displayOrder,
          question_group_id: questionGroupId || null,
          file: filePath,
        }

        const { error: insertError } = await supabase
          .from('questions')
          .insert(newQuestion)

        if (insertError) throw insertError
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save question')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4 font-display">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-light">
              <span className="material-symbols-outlined text-primary">
                {question ? 'edit_note' : 'add_circle'}
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {question ? '문제 수정' : '새 문제 등록'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                문제 이름 <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="문제 이름을 입력하세요"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                설명
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary resize-none"
                placeholder="문제에 대한 설명을 입력하세요 (선택사항)"
              />
            </div>

            <div>
              <label htmlFor="question_group" className="block text-sm font-medium text-gray-700 mb-2">
                문제 그룹
              </label>
              <select
                id="question_group"
                disabled={loadingGroups}
                value={questionGroupId || ''}
                onChange={(e) => setQuestionGroupId(e.target.value ? parseInt(e.target.value) : null)}
                className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50 disabled:bg-gray-100"
              >
                <option value="">미분류</option>
                {questionGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.book.name} &gt; {group.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">문제가 속할 그룹을 선택하세요</p>
            </div>

            <div>
              <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-2">
                표시 순서
              </label>
              <input
                id="display_order"
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="0"
              />
              <p className="mt-1 text-xs text-gray-500">낮은 숫자가 먼저 표시됩니다</p>
            </div>

            <div>
              <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-2">
                파일 첨부
              </label>
              <div className="mt-1">
                <label
                  htmlFor="file"
                  className="flex items-center justify-center w-full px-4 py-8 border-2 border-gray-200 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="text-center">
                    <span className="material-symbols-outlined text-gray-400 text-4xl mb-2">upload_file</span>
                    <p className="text-sm text-gray-600">
                      {file ? (
                        <span className="font-medium text-primary">{file.name}</span>
                      ) : question?.file && !file ? (
                        <span className="font-medium text-gray-700">
                          현재: {question.file.split('/').pop()}
                        </span>
                      ) : (
                        <>
                          <span className="font-medium text-primary">파일 선택</span>
                          <span className="text-gray-500"> 또는 드래그 앤 드롭</span>
                        </>
                      )}
                    </p>
                  </div>
                  <input
                    id="file"
                    type="file"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              {(file || question?.file) && (
                <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">info</span>
                  <span>새 파일을 선택하면 기존 파일이 대체됩니다</span>
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span className="material-symbols-outlined text-base">close</span>
              <span>취소</span>
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 border border-transparent rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                  <span>저장 중...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-base">
                    {question ? 'check' : 'add'}
                  </span>
                  <span>{question ? '수정' : '등록'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
